/**
 * Fetches a delegate token for the chatbot by calling the Parent Gateway API's
 * token exchange endpoint. This endpoint exchanges the user's access token for
 * a scoped token with only parent_chatbot permissions.
 *
 * @param userAccessToken - The user's current access token from OIDC authentication
 * @returns The delegate token for chatbot API access, or null if the exchange fails
 */
import { logger } from "./logger";

export async function fetchChatbotToken(userAccessToken: string): Promise<string | null> {
  try {
    // Token exchange is in ParentGatewayApi
    const apiUrl = (window as any).__config.API_URL;
    const tokenExchangeEndpoint = `${apiUrl}/api/v3/TokenExchange/chat-bot`;

    logger.debug("[Chatbot] Requesting delegate token from Parent Gateway API");
    logger.debug("[Chatbot] Token exchange endpoint:", tokenExchangeEndpoint);
    logger.debug("[Chatbot] User token length:", userAccessToken.length);
    logger.debug("[Chatbot] User token format: Bearer <token>");

    const response = await fetch(tokenExchangeEndpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${userAccessToken}`,
        "Content-Type": "application/json",
      },
    });

    logger.debug("[Chatbot] Token exchange response status:", response.status);
    logger.debug("[Chatbot] Response headers:", {
      contentType: response.headers.get("content-type"),
      contentLength: response.headers.get("content-length"),
    });

    if (!response.ok) {
      logger.error(`[Chatbot] Token exchange failed. Status: ${response.status} ${response.statusText}`);
      const errorText = await response.text();
      logger.error("[Chatbot] Error response:", errorText);
      return null;
    }

    const data = await response.json();
    logger.debug("[Chatbot] Delegate token received successfully");
    logger.debug("[Chatbot] Token type:", data.token_type);
    logger.debug("[Chatbot] Token expires in:", data.expires_in);

    return data.access_token ?? null;
  } catch (error) {
    logger.error("[Chatbot] Failed to fetch delegate token:", error);
    return null;
  }
}
